import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import MovieHero from './components/MovieHero';
import ReviewList from './components/ReviewList';
import {
  getMovieById,
  getRelatedMovies,
  getReviewsByMovie,
  getSpecialForMovie,
} from '@/lib/data';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  useEffect(() => {
    if (movie) {
      document.title = `《${movie.title}》详情介绍及影评解析 - 韩国理论片`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) {
        desc.setAttribute(
          'content',
          `${movie.title}（${movie.year}，${movie.director}导演）韩国剧情电影详情介绍与深度影评解析。${movie.summary}`,
        );
      }
    }
  }, [movie]);

  if (!movie) {
    return (
      <SiteLayout sidebar>
        <Breadcrumb items={[{ label: '电影分类', to: '/category' }, { label: '影片未找到' }]} />
        <EmptyState
          icon="ri-emotion-sad-line"
          title="没有找到这部影片"
          description="你访问的影片可能已下线或链接有误。可以返回分类列表浏览全部韩国剧情与理论电影。"
        />
        <div className="mt-5 flex justify-center">
          <Link
            to="/category"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 font-label text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回电影分类
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const reviews = getReviewsByMovie(movie.id);
  const related = getRelatedMovies(movie, 4);
  const special = getSpecialForMovie(movie.id);

  return (
    <SiteLayout sidebar>
      <Breadcrumb items={[{ label: '电影分类', to: '/category' }, { label: movie.title }]} />

      <MovieHero movie={movie} />

      {special && (
        <section className="mt-10 rounded-lg border border-accent-200/70 bg-accent-50/70 p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-accent-500 text-primary-950">
                <i className="ri-book-marked-line text-xl" aria-hidden="true" />
              </span>
              <div>
                <p className="font-label text-xs font-semibold text-accent-700">所属专题</p>
                <h2 className="mt-1 font-heading text-base font-bold text-foreground-950">{special.title}</h2>
                <p className="mt-1 text-sm text-foreground-600 line-clamp-2">{special.intro}</p>
              </div>
            </div>
            <Link
              to="/special"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 font-label text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              进入专题
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        </section>
      )}

      <section className="mt-12">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <i className="ri-file-text-line text-primary-500" aria-hidden="true" />
          影片简介与剧情摘要
        </h2>
        <div className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
          <p className="text-sm md:text-base leading-loose text-foreground-700">{movie.synopsis}</p>
        </div>
      </section>

      {reviews.length > 0 && <ReviewList reviews={reviews} />}

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-links-line text-accent-600" aria-hidden="true" />
            相关推荐
            <span className="font-label text-sm font-medium text-foreground-400">（同导演或同类型）</span>
          </h2>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}